import { apiService } from './apiService';

/**
 * Cached access to tenant/system settings. Updates clear the relevant cache
 * and re-fetch so every consumer (SettingsContext, dropdowns, colours) sees
 * the same server state after a save.
 */
class SettingsService {
  constructor() {
    this.cache = {
      systemSettings: null,
      companyConfig: null,
      enums: null,
      roles: null,
      departments: null
    };
    this.cacheExpiry = {
      systemSettings: null,
      companyConfig: null,
      enums: null,
      roles: null,
      departments: null
    };
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  isCacheValid(key) {
    return this.cache[key] && this.cacheExpiry[key] && Date.now() < this.cacheExpiry[key];
  }

  setCache(key, data) {
    this.cache[key] = data;
    this.cacheExpiry[key] = Date.now() + this.cacheDuration;
  }

  clearCache(key) {
    this.cache[key] = null;
    this.cacheExpiry[key] = null;
  }

  clearAllCache() {
    Object.keys(this.cache).forEach(key => this.clearCache(key));
  }

  async fetchCached(key, request) {
    if (this.isCacheValid(key)) return this.cache[key];
    const response = await request();
    this.setCache(key, response.data);
    return response.data;
  }

  // ---- System settings (systemSettings/global) ----
  getSystemSettings() {
    return this.fetchCached('systemSettings', () => apiService.settings.getSystem());
  }

  /** Deep-merged on the server; pass only the subtree you changed. */
  async updateSystemSettings(settings) {
    const response = await apiService.settings.updateSystem(settings);
    this.clearCache('systemSettings');
    const fresh = await this.getSystemSettings();
    return { ...response.data, settings: fresh };
  }

  // ---- Company configuration ----
  getCompanyConfiguration() {
    return this.fetchCached('companyConfig', () => apiService.settings.getCompany());
  }

  async updateCompanyConfiguration(config) {
    const response = await apiService.settings.updateCompany(config);
    this.clearCache('companyConfig');
    // Roles/departments are derived from the company configuration
    this.clearCache('roles');
    this.clearCache('departments');
    const fresh = await this.getCompanyConfiguration();
    return { ...response.data, configuration: fresh };
  }

  // ---- Configurable enums ----
  getConfigurableEnums() {
    return this.fetchCached('enums', () => apiService.settings.getEnums());
  }

  async updateConfigurableEnums(enums) {
    const response = await apiService.settings.updateEnums(enums);
    this.clearCache('enums');
    const fresh = await this.getConfigurableEnums();
    return { ...response.data, enums: fresh };
  }

  // ---- Roles & departments ----
  getRoles() {
    return this.fetchCached('roles', () => apiService.settings.getRoles());
  }

  getDepartments() {
    return this.fetchCached('departments', () => apiService.settings.getDepartments());
  }

  // ---- Colour maps derived from enums ----
  static DEFAULT_LEVEL_COLORS = {
    Low: { main: '#4caf50', light: '#81c784', dark: '#388e3c', contrastText: '#ffffff' },
    Medium: { main: '#ff9800', light: '#ffb74d', dark: '#f57c00', contrastText: '#ffffff' },
    High: { main: '#ff5722', light: '#ff8a65', dark: '#e64a19', contrastText: '#ffffff' },
    Critical: { main: '#d32f2f', light: '#ef5350', dark: '#c62828', contrastText: '#ffffff' }
  };

  async getSeverityColors() {
    try {
      const enums = await this.getConfigurableEnums();
      const colors = {};
      (enums.severityLevels || []).forEach(level => {
        if (level.enabled) colors[level.value] = level.color;
      });
      return colors;
    } catch (error) {
      return { ...SettingsService.DEFAULT_LEVEL_COLORS };
    }
  }

  async getStatusColors(category = 'detection') {
    try {
      const enums = await this.getConfigurableEnums();
      const colors = {};
      ((enums.statusOptions && enums.statusOptions[category]) || []).forEach(status => {
        if (status.enabled) colors[status.value] = status.color;
      });
      return colors;
    } catch (error) {
      return {
        New: '#2196f3',
        'In Progress': '#ff9800',
        'Under Review': '#9c27b0',
        Resolved: '#4caf50',
        'False Positive': '#757575'
      };
    }
  }

  async getPriorityColors() {
    try {
      const enums = await this.getConfigurableEnums();
      const colors = {};
      (enums.priorityLevels || []).forEach(level => {
        if (level.enabled) colors[level.value] = level.color;
      });
      return colors;
    } catch (error) {
      return { ...SettingsService.DEFAULT_LEVEL_COLORS };
    }
  }

  // ---- Formatted dropdown options ----
  async getFormattedRoles() {
    try {
      const roles = await this.getRoles();
      return roles.map(role => ({ value: role.name, label: role.displayName || role.name }));
    } catch (error) {
      return [];
    }
  }

  async getFormattedDepartments() {
    try {
      const departments = await this.getDepartments();
      return departments.map(dept => ({ value: dept.name, label: dept.displayName || dept.name }));
    } catch (error) {
      return [];
    }
  }

  async getFormattedSeverities() {
    try {
      const enums = await this.getConfigurableEnums();
      return (enums.severityLevels || [])
        .filter(s => s.enabled)
        .sort((a, b) => a.order - b.order)
        .map(s => ({ value: s.value, label: s.label, color: s.color }));
    } catch (error) {
      return [];
    }
  }

  async getFormattedStatuses(category = 'detection') {
    try {
      const enums = await this.getConfigurableEnums();
      return ((enums.statusOptions && enums.statusOptions[category]) || [])
        .filter(s => s.enabled)
        .sort((a, b) => a.order - b.order)
        .map(s => ({ value: s.value, label: s.label, color: s.color }));
    } catch (error) {
      return [];
    }
  }
}

const settingsService = new SettingsService();

export { settingsService };
